import React, { useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { TradeTabs } from 'common/components';

import { DEFAULT_TRADE_TAB } from 'common/constants';
import { useCustomer } from 'common/hooks';
import { TradeTabsType } from 'common/types';

import { routes } from 'routes/routes.constants';

import './index.css';

const Derivatives = React.lazy(
  () => import(/* webpackChunkName: "Derivatives" */ 'views/derivatives/list/Derivatives.base.container'),
);
const Spot = React.lazy(() => import(/* webpackChunkName: "Spot" */ 'views/spot/list/Spot.base.container'));
const Stocks = React.lazy(() => import(/* webpackChunkName: "Stocks" */ 'views/stocks/list/Stocks.base.container'));

const TradeNavigationContainer: React.FC<React.PropsWithChildren<{ storageKeyForSearch?: string }>> = ({
  storageKeyForSearch,
}) => {
  const { checkFeaturesAvailability } = useCustomer();

  const isSpotAvailable = useMemo(() => checkFeaturesAvailability('spot'), [checkFeaturesAvailability]);
  const isFuturesAvailable = useMemo(() => checkFeaturesAvailability('derivatives'), [checkFeaturesAvailability]);
  const isStocksAvailable = useMemo(() => checkFeaturesAvailability('stock'), [checkFeaturesAvailability]);

  const isSpotRoute = useRouteMatch(routes.getAssetPage());
  const isFuturesRoute = useRouteMatch(routes.getDerivativesAssetPage());
  const isStocksRoute = useRouteMatch(routes.getStocksDashboardPage());

  const activeTradeTabFromRoute: TradeTabsType = useMemo(() => {
    if (isSpotRoute) return 'spot';
    if (isFuturesRoute) return 'futures';
    if (isStocksRoute) return 'stocks';
    return DEFAULT_TRADE_TAB;
  }, [isSpotRoute, isFuturesRoute, isStocksRoute]);

  const [activeTab, updateActiveTab] = useState<TradeTabsType>(activeTradeTabFromRoute);

  const outerContainerClassName = 'quick-nav-list__lvl2-container';
  const innerContainerClassName = 'quick-nav-list__lvl3-container';
  const listClassName = 'quick-nav-list__assets-list';

  const renderTradeModule = useCallback(() => {
    switch (activeTab) {
      case 'spot':
        return isSpotAvailable ? (
          <Spot
            outerContainerClassName={outerContainerClassName}
            innerContainerClassName={innerContainerClassName}
            listClassName={listClassName}
            storageKeyForSearch={storageKeyForSearch}
          />
        ) : null;
      case 'futures':
        return isFuturesAvailable ? (
          <Derivatives
            outerContainerClassName={outerContainerClassName}
            innerContainerClassName={innerContainerClassName}
            listClassName={listClassName}
            storageKeyForSearch={storageKeyForSearch}
          />
        ) : null;
      case 'stocks':
        return isStocksAvailable ? (
          <Stocks
            outerContainerClassName={outerContainerClassName}
            innerContainerClassName={innerContainerClassName}
            listClassName={listClassName}
            storageKeyForSearch={storageKeyForSearch}
          />
        ) : null;
      default:
        return null;
    }
  }, [activeTab, isFuturesAvailable, isSpotAvailable, isStocksAvailable, storageKeyForSearch]);

  return (
    <>
      <TradeTabs
        tabsPersistanceInLocalStorage={false}
        activeTab={activeTab}
        setActiveTab={updateActiveTab}
        className='pt-16'
      />
      <React.Suspense fallback={null}>{renderTradeModule()}</React.Suspense>
    </>
  );
};

export default TradeNavigationContainer;
